import {
  Alert,
  Button,
  Center,
  Container,
  Divider,
  Grid,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  Title
} from '@mantine/core';
import { IconAlertCircle, IconCoin, IconDownload } from '@tabler/icons-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config/api';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import { useTokenManagement } from '../hooks/useTokenManagement';
import { VoiceProvider } from '../services/voiceService';

/**
 * TextToSpeech component for converting text to speech with multiple providers
 */
export function TextToSpeech() {
  const navigate = useNavigate();
  
  const {
    text,
    setText,
    voice,
    setVoice,
    provider,
    setProvider,
    providers,
    voices,
    isLoadingProviders,
    isLoadingVoices,
    isGenerating,
    audioUrl,
    handleGenerate,
    currentVoiceDetails,
    insufficientTokens,
    requiredTokens,
    availableTokens,
    clearTokenError,
    showBuyTokensModal,
    setShowBuyTokensModal,
    audioHistory,
    isLoadingHistory
  } = useTextToSpeech();
  
  const { userToken, formatTokenInfo } = useTokenManagement();
  const { formattedBalance, formattedExpiry } = formatTokenInfo();

  const handleNavigateToTokens = () => {
    navigate('/tokens');
  };

  return (
    <Container fluid px="md" py="md">
      <Stack gap="md">
        <Grid gutter="md">
          {/* Left column - Voice Selection (đã đổi vị trí) */}
          <Grid.Col span={4}>
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Title order={4}>Chọn giọng đọc</Title>
                
                {isLoadingVoices ? (
                  <Center py="md">
                    <Loader size="sm" />
                  </Center>
                ) : (
                  <Tabs defaultValue={voices[0]?.country.code}>
                    <Tabs.List>
                      {voices.map(group => (
                        <Tabs.Tab
                          key={group.country.code}
                          value={group.country.code}
                          leftSection={group.country.flag}
                        >
                          {group.country.name}
                        </Tabs.Tab>
                      ))}
                    </Tabs.List>

                    {voices.map(group => (
                      <Tabs.Panel key={group.country.code} value={group.country.code}>
                        <ScrollArea h={300} mt="md">
                          <Group gap="xs" wrap="wrap">
                            {group.voices.map(v => (
                              <Paper
                                key={v.id}
                                withBorder
                                p="xs"
                                style={{
                                  cursor: 'pointer',
                                  backgroundColor: voice === v.id ? '#f0f9ff' : 'white',
                                  width: '100px'
                                }}
                                onClick={() => setVoice(v.id)}
                              >
                                <Stack gap="xs" align="center">
                                  <Image
                                    src={v.image}
                                    alt={v.name}
                                    width={80}
                                    height={80}
                                    radius="md"
                                    fallbackSrc="https://placehold.co/80x80?text=No+Image"
                                  />
                                  <Text size="xs" fw={500} ta="center" lineClamp={2}>
                                    {v.name}
                                  </Text>
                                </Stack>
                              </Paper>
                            ))}
                          </Group>
                        </ScrollArea>
                      </Tabs.Panel>
                    ))}
                  </Tabs>
                )}
                
                {currentVoiceDetails?.audio_sample && (
                  <div>
                    <Text size="sm" fw={500} mb={5}>Mẫu giọng đọc:</Text>
                    <audio controls src={currentVoiceDetails.audio_sample} style={{ width: '100%' }} />
                  </div>
                )}
              </Stack>
            </Paper>
          </Grid.Col>

          {/* Middle column - Text Controls (đã đổi vị trí) */}
          <Grid.Col span={4}>
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                  <Stack gap={5}>
                    <Title order={3}>Chuyển văn bản thành giọng nói</Title>
                  </Stack>
                  <Group>
                    <Group gap={4}>
                      <IconCoin size={18} color="#FFD700" />
                      <Text fw={700} size="md">{userToken?.balance || 0} tokens</Text>
                    </Group>
                    <Button 
                      size="xs" 
                      color="yellow" 
                      variant="filled"
                      onClick={handleNavigateToTokens}
                      leftSection={<IconCoin size={14} />}
                    >
                      Mua
                    </Button>
                  </Group>
                </Group>
                
                <Divider />
                
                {/* Select provider */}
                <Title order={5}>Chọn server giọng</Title>
                
                {isLoadingProviders ? (
                  <Center py="md">
                    <Loader size="sm" />
                  </Center>
                ) : (
                  <Select
                    placeholder="Chọn server giọng"
                    value={provider}
                    onChange={(value) => value && setProvider(value as VoiceProvider)}
                    data={providers.map(p => ({ value: p.id, label: p.name }))}
                  />
                )}
                
                {/* Text input */}
                <Title order={5}>Nội dung</Title>
                
                <Textarea
                  placeholder="Nhập nội dung cần chuyển thành giọng nói..."
                  autosize
                  minRows={10}
                  maxRows={20}
                  value={text}
                  onChange={(e) => setText(e.currentTarget.value)}
                />
                
                {text && (
                  <Group>
                    <Text size="sm">
                      Số token cần thiết: <Text span fw={500}>{text.length}</Text>
                    </Text>
                    <Progress 
                      value={(userToken?.balance || 0) >= text.length ? 100 : (userToken?.balance || 0) / text.length * 100} 
                      size="sm"
                      color={(userToken?.balance || 0) >= text.length ? 'green' : 'red'}
                      style={{ width: '100%' }}
                    />
                    {(userToken?.balance || 0) < text.length && (
                      <Alert color="red" icon={<IconAlertCircle size={16} />} radius="sm">
                        <Group justify="space-between">
                          <Text>Bạn cần thêm {text.length - (userToken?.balance || 0)} token để tạo giọng nói</Text>
                          <Button 
                            size="xs" 
                            color="red" 
                            variant="light"
                            onClick={handleNavigateToTokens}
                            leftSection={<IconCoin size={14} />}
                          >
                            Mua token
                          </Button>
                        </Group>
                      </Alert>
                    )}
                  </Group>
                )}
                
                <Button 
                  onClick={handleGenerate} 
                  loading={isGenerating}
                  disabled={!text || !voice || isLoadingVoices}
                  fullWidth
                  size="md"
                >
                  {isGenerating ? 'Đang xử lý...' : 'Tạo giọng nói'}
                </Button>
              </Stack>
            </Paper>
          </Grid.Col>

          {/* Right column - Audio History */}
          <Grid.Col span={4}>
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Title order={4}>Lịch sử</Title>
                
                {isLoadingHistory ? (
                  <Center py="md">
                    <Loader size="sm" />
                  </Center>
                ) : audioHistory && audioHistory.length > 0 ? (
                  <ScrollArea h={400}>
                    <Stack gap="xs">
                      {audioHistory.map((audio) => {
                        // Tạo ngày hết hạn
                        const expiryDate = new Date(audio.expiresAt);
                        const now = new Date();
                        const hoursLeft = Math.round((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60));
                        
                        const audioUrl = `${BASE_URL}${audio.localPath.slice(7)}`;
                        
                        return (
                          <Paper key={audio._id} p="xs" withBorder>
                            <Stack gap="xs">
                              <Group justify="space-between">
                                <Text size="sm" fw={500} lineClamp={1} style={{ maxWidth: '70%' }}>
                                  {audio.text}
                                </Text>
                                <Text size="xs" c="dimmed">
                                  {new Date(audio.createdAt).toLocaleString()}
                                </Text>
                              </Group>
                              
                              <audio controls src={audioUrl} style={{ width: '100%' }} />
                              
                              <Group justify="space-between">
                                <Text size="xs" c="orange">
                                  Hết hạn trong: {hoursLeft} giờ
                                </Text>
                                <Button 
                                  component="a" 
                                  href={audioUrl} 
                                  download={`audio_${Date.now()}.mp3`}
                                  size="xs"
                                  variant="light"
                                  leftSection={<IconDownload size={14} />}
                                >
                                  Tải xuống
                                </Button>
                              </Group>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </ScrollArea>
                ) : (
                  <Center py="xl">
                    <Stack align="center">
                      <IconAlertCircle size={48} opacity={0.3} />
                      <Text c="dimmed" ta="center">Bạn chưa tạo file audio nào.</Text>
                    </Stack>
                  </Center>
                )}
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* Modal khi không đủ token */}
      <Modal
        opened={showBuyTokensModal}
        onClose={() => {
          setShowBuyTokensModal(false);
          clearTokenError();
        }}
        title="Không đủ token"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Bạn không có đủ token để tạo giọng nói này
          </Alert>
          <Text>
            Yêu cầu: <Text span fw={700}>{requiredTokens}</Text> tokens
          </Text>
          <Text>
            Hiện có: <Text span fw={700}>{availableTokens}</Text> tokens
          </Text>
          <Text>
            Cần thêm: <Text span fw={700}>{Math.max(0, requiredTokens - availableTokens)}</Text> tokens
          </Text>
          <Group mt="md">
            <Button 
              variant="outline" 
              onClick={() => setShowBuyTokensModal(false)}
              fullWidth
            >
              Hủy
            </Button>
            <Button 
              onClick={handleNavigateToTokens}
              fullWidth
            >
              Mua token
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
} 